/**
 * 정산 관리 공통 API 헬퍼 함수들
 */
import { apiClient } from "@/shared/api/client";
import { formatDateToISO } from "./dateUtils";

/**
 * 공통 최근 정산종료일자 조회 응답 DTO
 */
export interface LatestDateResponse {
    latestDate: string;
}

/**
 * 공통 API 응답 DTO
 */
export interface CommonApiResponse {
    success: boolean;
    message: string;
}

/**
 * 최근 정산종료일자 조회 (공통)
 * @param basePath API 기본 경로 (예: '/api/v1/partner-settlement')
 */
export const getLatestSettlementDate = async (basePath: string): Promise<LatestDateResponse> => {
    const response = await apiClient.get<LatestDateResponse>(`${basePath}/latest-date`);
    return response.data;
};

/**
 * 엑셀 파일 업로드 (공통)
 * @param basePath API 기본 경로
 * @param file 업로드할 파일
 * @param userId 사용자 ID
 */
export const uploadSettlementFile = async (
    basePath: string,
    file: File,
    userId: string
): Promise<CommonApiResponse> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('userId', userId);

    const response = await apiClient.post<CommonApiResponse>(
        `${basePath}/upload`,
        formData,
        {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            timeout: 300000
        }
    );
    return response.data;
};

/**
 * 엑셀 다운로드 URL 생성 헬퍼
 * @param basePath API 기본 경로
 * @param params 쿼리 파라미터 객체
 * @returns 완성된 URL
 */
export const buildExcelDownloadUrl = (
    basePath: string,
    params: Record<string, string | undefined>
): string => {
    const queryParams: string[] = [];
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') {
            // 날짜 필드는 ISO 형식으로 변환
            const formattedValue = key.includes('Date') ? formatDateToISO(value) : value;
            queryParams.push(`${key}=${encodeURIComponent(formattedValue)}`);
        }
    });

    return `${basePath}/excel?${queryParams.join('&')}`;
};

/**
 * 엑셀 다운로드 (공통)
 * @param url 다운로드 URL
 */
export const downloadExcelFile = async (url: string): Promise<Blob> => {
    const response = await apiClient.get(url, {
        responseType: 'blob'
    });
    return response.data;
};
